import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Link, useParams } from 'react-router-dom';
import { Search, Zap, MessageSquare, BarChart3, Users, Settings, Activity, Shield, Clock, Star, CheckCircle, ArrowRight, KeyRound, Plug, Lock, Globe, Terminal, type LucideIcon } from 'lucide-react';
import { ClosingCTA } from '../components/ClosingCTA';
import { Markdown } from '../components/Markdown';
import { fetchIntegration, getCachedIntegration, type IntegrationDetail as Detail, type CatalogMethod } from '../lib/catalog';
import { trackSignupClick, withAnalyticsParams } from '../lib/analytics';

// Map a lucide icon NAME (stored in page_content) to a component, with a
// safe fallback so unknown names never crash the page.
const ICONS: Record<string, LucideIcon> = {
  Search,
  Zap,
  MessageSquare,
  BarChart3,
  Users,
  Settings,
  Activity,
  Shield,
  Clock,
  Star,
  CheckCircle
};
function iconFor(name?: string): LucideIcon {
  return name && ICONS[name] || Zap;
}

// Friendly labels + icons for the auth mechanisms we support.
const METHOD_META: Record<string, {
  label: string;
  icon: LucideIcon;
  blurb: string;
}> = {
  oauth2: {
    label: 'One-click connect',
    icon: Plug,
    blurb: 'Secure OAuth — log in and authorize, no keys to copy.'
  },
  mcp: {
    label: 'MCP server',
    icon: Plug,
    blurb: 'Structured tool access via the Model Context Protocol.'
  },
  api_key: {
    label: 'API key',
    icon: KeyRound,
    blurb: 'Paste an API key — Giga stores it encrypted.'
  },
  pat: {
    label: 'Personal access token',
    icon: KeyRound,
    blurb: 'Connect with a personal access token.'
  },
  password: {
    label: 'Username & password',
    icon: Lock,
    blurb: 'Credentials stored encrypted in the vault.'
  },
  connection_string: {
    label: 'Connection string',
    icon: Terminal,
    blurb: 'Connect via a database/service connection string.'
  },
  browser_session: {
    label: 'Browser login',
    icon: Globe,
    blurb: 'Giga logs in through a secure browser session.'
  },
  jwt_byo: {
    label: 'JWT (bring your own)',
    icon: KeyRound,
    blurb: 'Sign requests with your own key.'
  },
  cli: {
    label: 'CLI',
    icon: Terminal,
    blurb: 'Authorize through the service CLI.'
  }
};
function methodMeta(key: string) {
  return METHOD_META[key] || {
    label: key,
    icon: Plug,
    blurb: ''
  };
}
const SECTION_EYEBROW = 'text-xs font-semibold tracking-[5px] uppercase text-neutral-400 mb-5 block';
export function IntegrationDetail() {
  const {
    slug
  } = useParams<{
    slug: string;
  }>();
  // Seed from the build-time prime cache when present (SSR/prerender): undefined
  // = not primed (client fetches), null = known-missing, object = ready.
  const primed = slug ? getCachedIntegration(slug) : undefined;
  const [data, setData] = useState<Detail | null>(primed ?? null);
  const [status, setStatus] = useState<'loading' | 'ready' | 'notfound' | 'error'>(primed === undefined ? 'loading' : primed === null ? 'notfound' : 'ready');
  useEffect(() => {
    if (!slug) return;
    let cancelled = false;
    setStatus('loading');
    fetchIntegration(slug).then(d => {
      if (cancelled) return;
      if (!d) {
        setStatus('notfound');
        return;
      }
      setData(d);
      setStatus('ready');
    }).catch(() => !cancelled && setStatus('error'));
    return () => {
      cancelled = true;
    };
  }, [slug]);

  // Set <title> + meta description from the catalog (baked in by the prerenderer).
  useEffect(() => {
    if (!data) return;
    document.title = data.seo_title || `${data.display_name} Integration — Giga`;
    const desc = data.seo_description || data.tagline || data.description || `Connect ${data.display_name} to Giga and use it through Slack.`;
    let meta = document.querySelector('meta[name="description"]');
    if (!meta) {
      meta = document.createElement('meta');
      meta.setAttribute('name', 'description');
      document.head.appendChild(meta);
    }
    meta.setAttribute('content', desc);
  }, [data]);
  if (status === 'loading') {
    return <div data-loading="true" className="min-h-screen bg-white flex items-center justify-center text-neutral-400" data-id="element-9545" data-mp="1mptgm1mntin3">
        Loading…
      </div>;
  }
  if (status === 'notfound' || status === 'error') {
    return <div className="min-h-screen bg-white flex flex-col items-center justify-center gap-4 px-6 text-center" data-id="element-9546" data-mp="1wfczv8eabeih">
        <h1 className="text-3xl font-bold text-[#333333] font-title" data-id="element-9547" data-mp="yugz1ybfetx8">
          {status === 'notfound' ? 'Integration not found' : 'Something went wrong'}
        </h1>
        <p className="text-[#475569]" data-id="element-9548" data-mp="18qu0xjh4ke32">
          {status === 'notfound' ? "We couldn't find that integration." : "We couldn't load this integration right now."}
        </p>
        <Link to="/integrations" className="inline-flex items-center gap-1.5 text-[#EA580C] font-semibold hover:underline" data-id="element-9549" data-mp="2k68vyhprwvvj">
          View all integrations <span aria-hidden="true" data-id="element-9550" data-mp="2o6j1jt9nlk9v">→</span>
        </Link>
      </div>;
  }
  const d = data!;
  const pc = d.page_content || {};
  const features = pc.features || [];
  const prompts = pc.example_prompts || [];
  const faqs = pc.faqs || [];
  const heading = d.tagline || `Use ${d.display_name} through Slack with Giga.`;
  return <div className="min-h-screen bg-white font-body selection:bg-[#EA580C]/20 selection:text-[#333333]" data-id="element-9551" data-mp="3q2jcwomgpn0">
      {/* Hero */}
      <section className="relative w-full bg-[#FAFAFA] pt-40 pb-32 md:pb-48 overflow-hidden flex flex-col items-center justify-center border-b border-neutral-100" data-id="element-9552" data-mp="3pzdkkzndljvo">
        <div className="max-w-[1280px] mx-auto px-6 md:px-10 relative z-10 text-center" data-id="element-9553" data-mp="3kyz917mkfrsw">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6
        }} className="flex flex-col items-center" data-id="element-9554" data-mp="3gz8mxh7zhzup">
            <div className="inline-flex items-center gap-3 bg-white border border-neutral-200 px-4 py-2 rounded-full mb-8 shadow-sm" data-id="element-9555" data-mp="1f4zm19dqbdcu">
              {d.logo_url ? <img src={d.logo_url} alt="" className="w-6 h-6 rounded object-contain" data-id="element-9556" data-mp="1tbt44h6qya8o" /> : <div className="w-6 h-6 rounded bg-[#FFEDD5] text-[#EA580C] flex items-center justify-center text-xs font-bold" data-id="element-9557" data-mp="1y446dqoym84e">
                  {d.display_name.charAt(0)}
                </div>}
              <span className="text-sm font-medium text-[#333333]" data-id="element-9558" data-mp="1pb0tpkm51zat">
                <span className="font-bold" data-id="element-9559" data-mp="1p966fzne9v3k">{d.display_name}</span> × Giga
              </span>
            </div>

            <h1 className="font-title text-4xl md:text-6xl lg:text-[72px] font-bold tracking-tight text-[#333333] mb-8 leading-[1.05] max-w-5xl mx-auto" data-id="element-9560" data-mp="2h2gfjcfvfhzz">
              {heading}
            </h1>

            {d.description && <p className="text-xl md:text-2xl text-[#475569] mb-10 max-w-3xl mx-auto leading-relaxed" data-id="element-9561" data-mp="281ib4t1it8gv">
                {d.description}
              </p>}

            <div className="flex flex-col sm:flex-row items-center justify-center gap-4" data-id="element-9562" data-mp="rm787kh20f3k">
              <a href={withAnalyticsParams('https://app.getgiga.com/signup')} onClick={() => trackSignupClick('integration-detail', `Connect ${d.display_name}`)} className="inline-flex items-center justify-center bg-[#EA580C] hover:bg-[#C2410C] text-white px-8 py-4 rounded-full font-bold text-lg transition-colors shadow-lg shadow-[#EA580C]/20" data-id="element-9563" data-mp="wo6ccwkmbg1k">
                Connect {d.display_name}
                <ArrowRight size={20} className="ml-2" data-id="element-9564" data-mp="t5z45ppla3h4" />
              </a>
              <Link to="/integrations" className="inline-flex items-center gap-1.5 text-[#333333] font-semibold px-4 py-4 hover:text-[#EA580C] transition-colors" data-id="element-9565" data-mp="5ebltw6e80qg">
                View all integrations <span aria-hidden="true" data-id="element-9566" data-mp="f4mshqhgoo11">→</span>
              </Link>
            </div>
          </motion.div>
        </div>
        <div className="absolute bottom-0 left-0 w-full h-32 pointer-events-none bg-gradient-to-t from-white to-transparent" data-id="element-9567" data-mp="ady1sddlvv2y" />
      </section>

      {/* Features */}
      {features.length > 0 && <section className="w-full bg-white py-28 md:py-40" data-id="element-9568" data-mp="3dvojs4y49tav">
          <div className="max-w-[1280px] mx-auto px-6 md:px-10" data-id="element-9569" data-mp="3dt2bu47bdbs4">
            <div className="text-center mb-16 md:mb-20" data-id="element-9570" data-mp="2tanuqm3meoqe">
              <span className={SECTION_EYEBROW} data-id="element-9571" data-mp="2xaewfkuoqp9p">What you can do</span>
              <h2 className="text-4xl md:text-5xl font-title font-bold text-[#333333] mb-6 tracking-tight" data-id="element-9572" data-mp="2xcht2nqcfgne">
                Everything {d.display_name}, right in Slack.
              </h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8" data-id="element-9573" data-mp="376hvuiy5k9sy">
              {features.map((feature, i) => {
            const Icon = iconFor(feature.icon);
            return <motion.div key={i} initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.5,
              delay: i * 0.1
            }} className="bg-[#FAFAFA] rounded-2xl p-6 md:p-8 border border-neutral-200 shadow-sm flex flex-col" data-id="element-9574" data-mp="3g7g2l8zz6vf8" data-mpi={i}>
                    <div className="w-12 h-12 rounded-xl bg-[#FFEDD5] text-[#EA580C] flex items-center justify-center mb-6" data-id="element-9575" data-mp="1o9wpmysb0sr8">
                      <Icon size={24} data-id="element-9576" data-mp="1ja1xkrwslx4l" />
                    </div>
                    <h3 className="text-xl font-bold text-[#333333] mb-3" data-id="element-9577" data-mp="1ec0gudmua9wa">
                      {feature.title}
                    </h3>
                    <p className="text-[#475569] leading-relaxed" data-id="element-9578" data-mp="19dyvtn5us0i6">
                      {feature.description}
                    </p>
                  </motion.div>;
          })}
            </div>
          </div>
        </section>}

      {/* Example prompts */}
      {prompts.length > 0 && <section className="w-full bg-[#FAF8F4] py-28 md:py-40 border-y border-neutral-200" data-id="element-9579" data-mp="1xacn9rmd95o6">
          <div className="max-w-[1280px] mx-auto px-6 md:px-10" data-id="element-9580" data-mp="1scb6xegbrkge">
            <div className="text-center mb-16 md:mb-20" data-id="element-9581" data-mp="1s9ozrfxcj6yp">
              <span className={SECTION_EYEBROW} data-id="element-9582" data-mp="2ps754g1m4dbv">Examples</span>
              <h2 className="text-4xl md:text-5xl font-title font-bold text-[#333333] mb-6 tracking-tight" data-id="element-9583" data-mp="2l0p63xj5gmtj">
                See it in action.
              </h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8 max-w-5xl mx-auto" data-id="element-9584" data-mp="nqb8e4e436qg">
              {prompts.map((item, i) => <motion.div key={i} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: i * 0.1
          }} className="bg-white rounded-2xl p-6 md:p-8 border border-neutral-200 shadow-sm" data-id="element-9585" data-mp="xm4ry7q900hq" data-mpi={i}>
                  <div className="flex items-start gap-4" data-id="element-9586" data-mp="12joerw10hlla">
                    <div className="w-8 h-8 rounded-lg bg-[#EA580C]/10 text-[#EA580C] flex items-center justify-center shrink-0 mt-1" data-id="element-9587" data-mp="1gbkv72tbi71u">
                      <MessageSquare size={16} data-id="element-9588" data-mp="1bg5hqbz82d5y" />
                    </div>
                    <div data-id="element-9589" data-mp="1qblj7224tbcd">
                      <p className="text-base font-mono text-[#EA580C] mb-3" data-id="element-9590" data-mp="1q8pfex3lrsg0">
                        {item.prompt}
                      </p>
                      <div className="flex items-start gap-2" data-id="element-9591" data-mp="24xipu0u684w6">
                        <ArrowRight size={14} className="text-[#475569] mt-1 shrink-0" data-id="element-9592" data-mp="28zdrj6g0kkah" />
                        <p className="text-[#475569] text-sm" data-id="element-9593" data-mp="28x1cb11n4o14">{item.result}</p>
                      </div>
                    </div>
                  </div>
                </motion.div>)}
            </div>
          </div>
        </section>}

      {/* Connection methods */}
      {d.methods && d.methods.length > 0 && <section className="w-full bg-white py-28 md:py-40" data-id="element-9594" data-mp="1l7ylb2l49ity">
          <div className="max-w-[1280px] mx-auto px-6 md:px-10" data-id="element-9595" data-mp="1q22ulshuu2y7">
            <div className="text-center mb-16 md:mb-20" data-id="element-9596" data-mp="1c9mxf8bmwz57">
              <span className={SECTION_EYEBROW} data-id="element-9597" data-mp="1c70ignmezhcy">How to connect</span>
              <h2 className="text-4xl md:text-5xl font-title font-bold text-[#333333] mb-6 tracking-tight" data-id="element-9598" data-mp="1h8zltxi5me9w">
                Connect the way that suits you.
              </h2>
            </div>
            <div className="space-y-6 max-w-3xl mx-auto" data-id="element-9599" data-mp="178z0k60q5o32">
              {d.methods.map((m: CatalogMethod) => {
            const meta = methodMeta(m.key);
            const Icon = meta.icon;
            const recommended = d.recommended_method === m.key || m.preferred;
            const body = m.setup_instructions || meta.blurb || m.description || '';
            return <div key={m.key} className="bg-[#FAFAFA] rounded-2xl p-6 md:p-8 border border-neutral-200 shadow-sm" data-id="element-9600" data-mp="9gr7m6tvrpwx">
                    <div className="flex items-center gap-4 mb-4" data-id="element-9601" data-mp="174j5l30riv5k">
                      <div className="w-11 h-11 rounded-xl bg-white border border-neutral-200 text-[#EA580C] flex items-center justify-center shrink-0" data-id="element-9602" data-mp="1256l2oxd6065">
                        <Icon size={20} data-id="element-9603" data-mp="x4s8ctkti21s" />
                      </div>
                      <h3 className="text-lg font-bold text-[#333333]" data-id="element-9604" data-mp="s7idgyensy6k">
                        {meta.label}
                      </h3>
                      {recommended && <span className="text-[10px] font-bold uppercase tracking-wider text-[#EA580C] bg-[#FFEDD5] px-2 py-1 rounded-full" data-id="element-9605" data-mp="3hxlxmlpkxftl">
                          Recommended
                        </span>}
                      {m.doc_url && <a href={m.doc_url} target="_blank" rel="noopener noreferrer" className="ml-auto text-sm text-[#EA580C] font-medium hover:underline shrink-0" data-id="element-9606" data-mp="1xe9u1y16hqxi">
                          Docs →
                        </a>}
                    </div>
                    {body && <div className="text-sm" data-id="element-9607" data-mp="275nxui05xcwg">
                        <Markdown data-id="element-9608" data-mp="2c3g12869clfy">{body}</Markdown>
                      </div>}
                  </div>;
          })}
            </div>
          </div>
        </section>}

      {/* FAQ */}
      {faqs.length > 0 && <section className="w-full bg-[#FAF8F4] py-28 md:py-40 border-t border-neutral-200" data-id="element-9609" data-mp="2l446wflhzfhr">
          <div className="max-w-[860px] mx-auto px-6 md:px-10" data-id="element-9610" data-mp="2q2xn4fs65klm">
            <div className="text-center mb-16" data-id="element-9611" data-mp="2q25yxq0wip5d">
              <span className={SECTION_EYEBROW} data-id="element-9612" data-mp="qqcy1iazsznd">FAQ</span>
              <h2 className="text-4xl md:text-5xl font-title font-bold text-[#333333] mb-6 tracking-tight" data-id="element-9613" data-mp="qrxvpgslsypg">
                Common questions.
              </h2>
            </div>
            <div className="space-y-4" data-id="element-9614" data-mp="2m4gpfrc5xkv4">
              {faqs.map((item, i) => <details key={i} className="bg-white rounded-2xl border border-neutral-200 shadow-sm overflow-hidden group" data-id="element-9615" data-mp="2h7gn9rifp29a" data-mpi={i}>
                  <summary className="flex items-center justify-between p-6 cursor-pointer text-[#333333] font-bold text-lg hover:text-[#EA580C] transition-colors" data-id="element-9616" data-mp="2v5eywp5dekl3">
                    {item.q}
                    <span className="text-[#EA580C] group-open:rotate-45 transition-transform text-2xl shrink-0 ml-4" data-id="element-9617" data-mp="23gbsp3txb05t">
                      +
                    </span>
                  </summary>
                  <div className="px-6 pb-6 text-sm" data-id="element-9618" data-mp="2c8vrq5e4n6r6">
                    <Markdown data-id="element-9619" data-mp="27h5i61n0kn0t">{item.a}</Markdown>
                  </div>
                </details>)}
            </div>
          </div>
        </section>}

      <ClosingCTA data-id="element-9620" data-mp="1jq8e1b1gmu04" />
    </div>;
}